import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { useMySeries, useCreateSeries } from '../api/queries/series';
import { AppImage } from '../components/common/AppImage';
import { EmptyState } from '../components/common/EmptyState';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import { formatDistanceToNow } from '../lib/dates';
import MenuBookIcon from '@mui/icons-material/MenuBook';

export function SeriesListRoute() {
  const { data, isLoading, isError } = useMySeries();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Series
        </Typography>
        <Button variant="contained" onClick={() => setCreateOpen(true)}>
          New Series
        </Button>
      </Stack>

      {isLoading && <CircularProgress />}
      {isError && <Alert severity="error">Failed to load series.</Alert>}

      <Grid container spacing={2}>
        {(data ?? []).map((s) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={s.id}>
            <Card
              sx={{ cursor: 'pointer', height: '100%' }}
              onClick={() =>
                navigate({
                  to: '/series/$seriesId',
                  params: { seriesId: s.id },
                })
              }
            >
              <AppImage
                src={s.coverImageUrl}
                alt={s.name}
                width="100%"
                aspectRatio="16/9"
                fallbackIcon={
                  <MenuBookIcon sx={{ color: 'grey.400', fontSize: 48 }} />
                }
              />
              <CardContent>
                <Typography variant="h6" noWrap>
                  {s.name}
                </Typography>
                {s.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {s.description}
                  </Typography>
                )}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: 1 }}
                >
                  <Badge
                    badgeContent={s.bookCount}
                    color="primary"
                    showZero
                    max={999}
                  >
                    <MenuBookIcon fontSize="small" color="action" />
                  </Badge>
                  <Typography variant="caption" color="text.secondary">
                    {formatDistanceToNow(s.updatedAt)}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {!isLoading && (data ?? []).length === 0 && (
        <EmptyState
          icon={<CollectionsBookmarkIcon sx={{ fontSize: 64 }} />}
          title="Create your first series"
          description="Group related books into a series with shared characters, worlds, and style settings. Drag to reorder books within the series."
          ctaLabel="New Series"
          onCtaClick={() => setCreateOpen(true)}
        />
      )}

      <CreateSeriesDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </Paper>
  );
}

function CreateSeriesDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const create = useCreateSeries();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = async () => {
    if (!name.trim()) return;
    const series = await create.mutateAsync({
      name: name.trim(),
      description: description.trim() || undefined,
    });
    setName('');
    setDescription('');
    onClose();
    navigate({
      to: '/series/$seriesId',
      params: { seriesId: series.id },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New Series</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            required
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!name.trim() || create.isPending}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
